import get from 'lodash/get';
import { observer } from 'mobx-react';
import { useEffect, useReducer, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  MinMaxItem,
  MinMaxItemValuesHandler,
} from 'components/elements/minMaxInput';
import { NewPlpTypes } from 'lib/apis/services/NewPlpListiing';
import { useOwnTranslate } from 'lib/hooks/useOwnTranslate';

interface Props<T extends NewPlpTypes.RangeValue = NewPlpTypes.RangeValue>
  extends Omit<Partial<HTMLInputElement>, 'defaultValue' | 'value'> {
  data: NewPlpTypes.RangeFilterValueType;
  name: string;
  defaultValue?: T;
  label: {
    min: string;
    max: string;
  };
  value?: T;
  onChange?: (_value: T) => void;
  invisibleItems?: boolean;
}

type MinMaxValues = {
  min: string | number;
  max: string | number;
  maxValues: number[];
  computedMaxValues: (number | null)[];
};

const reducer = (
  state: MinMaxValues,
  action: {
    type: 'maxValueChange' | 'minValueChange';
    payload: any;
  }
): MinMaxValues => {
  switch (action.type) {
    case 'minValueChange':
      return {
        ...state,
        min: action.payload,
        computedMaxValues: state.maxValues.filter((value) => {
          return value && value > parseInt((action.payload as string) || '0');
        }),
      };
    case 'maxValueChange':
      return {
        ...state,
        max: action.payload,
      };
    default:
      throw new Error('');
  }
};

function Range({ name, data, invisibleItems = false, ...restProps }: Props) {
  const methods = useFormContext();
  const { t } = useOwnTranslate('global');
  const minValuesRef = useRef<MinMaxItemValuesHandler>(null);
  const maxValuesRef = useRef<MinMaxItemValuesHandler>(null);

  const { max: maxValue } = methods.watch(name) as NewPlpTypes.RangeValue;

  const [states, setState] = useReducer(reducer, {
    min: restProps.defaultValue?.min || '',
    max: restProps.defaultValue?.max || '',
    maxValues: data.maxValues,
    computedMaxValues:
      data.maxValues.filter(
        (value) =>
          value && value > (((restProps.defaultValue?.min as number) || 0) ?? 0)
      ) || [],
  });

  const minValueOnChangeHandler = (value: string | number) => {
    setState({ type: 'minValueChange', payload: value });
    const fieldValue = {
      min: value || null,
      max: maxValue,
    };

    if (maxValuesRef.current) {
      if (maxValue) {
        if (+value >= +maxValue) {
          maxValuesRef.current.value = '';
          fieldValue.max = null;
        }
      }
    }
    methods.setValue(name, fieldValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    // methods.trigger(name);
  };

  const onFocusChangeHandler = (type: 'minValue' | 'maxValue' = 'minValue') => {
    if (!invisibleItems) {
      if (type === 'minValue') {
        if (minValuesRef.current) minValuesRef.current.setVisibity(true);
        if (maxValuesRef.current) maxValuesRef.current.setVisibity(false);
      } else {
        if (minValuesRef.current) minValuesRef.current.setVisibity(false);
        if (maxValuesRef.current) maxValuesRef.current.setVisibity(true);
      }
    }
  };

  const minValueOnClickHandler = () => {
    if (minValuesRef.current) minValuesRef.current.setVisibity(true);
    if (maxValuesRef.current) {
      maxValuesRef.current.setVisibity(false);
      maxValuesRef.current.focus();
    }
  };

  useEffect(() => {
    if (minValuesRef.current) {
      minValuesRef.current.setVisibity(true);
    }
  }, [minValuesRef]);
  return (
    <Controller
      control={methods.control}
      name={name}
      defaultValue={restProps.defaultValue}
      render={({ field: { name: fieldName, value } }) => {
        return (
          <div className="flex flex-col space-y-4">
            <div className="flex items-start justify-center space-x-2 rtl:space-x-reverse">
              <MinMaxItem
                ref={minValuesRef}
                onFocusChange={() => onFocusChangeHandler()}
                name={`${fieldName}_GE`}
                itemValues={data.minValues}
                invisibleItems={invisibleItems}
                lable={restProps.label.min}
                postFix={data.unit || ''}
                disabled={!!restProps.disabled}
                defaultValue={get(value, 'min', '')}
                onClickItem={() => {
                  if (!invisibleItems) {
                    minValueOnClickHandler();
                  }
                }}
                onChange={(_value) => {
                  minValueOnChangeHandler(+_value);
                  if (restProps.onChange) restProps.onChange(value);
                  // maxValuesRef.current?.focus();
                }}
              />
              <p className="!mx-3 text-lg font-bold text-grey-600 pt-2">
                {t('to')}
              </p>
              <MinMaxItem
                itemValues={states.computedMaxValues}
                lable={restProps.label.max}
                name={`${fieldName}_LE`}
                ref={maxValuesRef}
                onFocusChange={() => onFocusChangeHandler('maxValue')}
                disabled={!!restProps.disabled}
                defaultValue={get(value, 'max', '')}
                postFix={data.unit || ''}
                invisibleItems={invisibleItems}
                onChange={(_value) => {
                  setState({ type: 'maxValueChange', payload: _value });
                  if (maxValue !== +_value) {
                    methods.setValue(
                      fieldName,
                      { ...value, max: +_value || null },
                      {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      }
                    );
                  }
                  if (restProps.onChange) restProps.onChange(value);
                }}
              />
            </div>
          </div>
        );
      }}
    />
  );
}

export default observer(Range);
